/**
 * CalendarBodyRow — `calendar-body` layout. The calendar's single
 * timescale-polymorphic body: its layout plugin renders the timed
 * timeline (+ all-day strip) for week/day and the month grid for month,
 * reading `sheet.timescale` at render. Replaces the template-swapped
 * `timeline[column]` ⇄ `grid` bodies so the layout survives hydration
 * (forking) — the switch is render-time, not a childspec re-key.
 *
 * See row-arch-reference/hydration-stable-layout.md.
 */
import type { Option } from "../../core/option.ts";
import type { JsonObject } from "../../core/json.ts";
import type { ModelRow } from "../model-row.ts";
import { AliasRow } from "./alias-row.ts";
import { LayoutRow } from "./layout-row.ts";
import { TimedGridConfig } from "./timed-grid-config.ts";
import type { SwitchCase, SwitchConfig } from "./switch-config.ts";

/**
 * The `calendar-body` layout row: a calendar's one body, which renders the timed timeline for a day
 * or week and the month grid for a month.
 */
export class CalendarBodyRow extends LayoutRow {
  // Polymorphic body — the render branch (timeline vs grid) is chosen by
  // the `calendar-body` layout plugin from `sheet.timescale`, not by type.

  /**
   * Timed-grid layout config (hours window / timezone / style / view). The
   * layout reads this off the bound body row instead of reaching UP to a
   * `CalendarAliasRow` ancestor — the inverted dependency the calendar split
   * left behind.
   *
   * Resolved as a cascade over this body's OWN attribute bag, with a
   * read-compat fallback to the parent calendar alias's top-level bag (where
   * `startHr`/`endHr`/`type`/`timezone`/`disableMonthlyView` historically
   * persist). The body's own attrs win, so once config is seeded on / written
   * to the body the alias fallback becomes pure legacy. Keeping the fallback
   * HERE — on the calendar-specific body variant — means the generic layout
   * plugin never imports `CalendarAliasRow`.
   */
  timedConfig(): TimedGridConfig {
    const own = this.attributes();
    const aliasAttrs = this.#nearestAliasAttrs();
    // Cascade (highest wins): the body's own bag (forked/flattened config) →
    // the alias's `children["calendar-body"]` override slot → the alias's
    // legacy top-level → the row-type default (in `TimedGridConfig.fromAttributes`).
    const aliasCascade = aliasAttrs
      ? TimedGridConfig.withChildOverride(aliasAttrs, TimedGridConfig.BODY_PRESET)
      : {};
    const merged = { ...aliasCascade, ...own };
    return TimedGridConfig.fromAttributes(merged);
  }

  /**
   * Generic {@link SwitchConfig} for the `switch` layout plugin: maps this
   * calendar body's timed-grid config onto candidate layouts — `grid` (month)
   * when the sheet is monthly AND monthly view is enabled, else the timed
   * `timeline[column]` (which itself hides for a `list` calendar). This is the
   * calendar's ONLY knowledge of the switch; the `CalendarBodyLayoutPlugin` plugin is
   * domain-neutral and just consumes the cases. Equivalent to the old
   * `effectiveView`/`disableMonthlyView`/`calendarType` branching, now expressed
   * as data.
   */
  switchConfig(): SwitchConfig {
    const cfg = this.timedConfig();
    const cases: SwitchCase[] = [];
    if (cfg.calendarType !== "list" && !cfg.disableMonthlyView) {
      cases.push({ layout: "grid", when: { timeScale: "month" } });
    }
    cases.push({ layout: "timeline[column]", when: "default" });
    return { cases };
  }

  /** The nearest ancestor `AliasRow`'s attribute bag, or null (e.g. forked). */
  #nearestAliasAttrs(): Option<JsonObject> {
    let cursor: Option<ModelRow> = this.parent;
    while (cursor != null) {
      if (cursor instanceof AliasRow) {
        return cursor.attributes();
      }
      cursor = cursor.parent;
    }
    return null;
  }
}
